import { Box, Button, Container, Grid, Card, TextField, IconButton, Tooltip } from '@mui/material';

import { Formik, Form } from 'formik';
import { FC, useState } from 'react';
import { Loader, Page } from '..';
import * as Yup from 'yup';
import { useToastContext } from '../../hooks';
import { FileUploader } from '../file/file-uploader';
import { Save, GetApp, InfoOutlined } from '@mui/icons-material';
import { ConfirmationDialogue } from '../modal/ConfirmationDialogue';
import { makeStyles } from '@mui/styles';
import { Maybe } from '../../models';
import { theme } from '../../styles';

const Schema = Yup.object().shape({
  importName: Yup.string().required('Required'),
  fileName: Yup.string().required('Required'),
});

interface IUploadHandlerProps
{
    title:string;
    loaderMessage:string;
    createEndpoint: (importName:string, uploadedFile:any)=>Promise<any>;
    /** When set, renders a "Download Template" link to this (app-hosted) file. */
    templateDownloadUrl?: string;
    /** When set, renders an info icon linking to the documentation (e.g. wiki) page. */
    documentationUrl?: string;
}

export const UploadHandler: FC<IUploadHandlerProps> = (props:IUploadHandlerProps) => {
  // Non-state
  const { showToast } = useToastContext();
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [errorsList, setErrorsList] = useState<Maybe<string[]>>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successfulRecords, setSuccessfulRecords] = useState(0);

  const classes = useStyles();

  return (
    <Page title={props.title}>
      <Container maxWidth={false} disableGutters>
        <>
          <Formik
            enableReinitialize={true}
            initialValues={{
              importName: '',
              fileName: '',
            }}
            validationSchema={Schema}
            onSubmit={async (values, actions) => {
              try {
                const res = await props.createEndpoint(values.importName, uploadedFile);
                if (res?.Detail) {
                  showToast('error', `Error, ${res.Detail}`);
                  setErrorsList(res.Detail.split('\r\n'));
                  return;
                } else if (res?.errors?.length > 0 || res?.validationErrors?.length > 0) {
                  const issues = res.validationErrors.concat(res.errors);
                  const summary = `Successfully imported ${res?.successCount ?? 0} record(s). ${issues.length} row(s) skipped:\r\n`;
                  setErrorsList([summary, ...issues.map((error: string) => `${error}\r\n`)]);
                  return;
                }
                setSuccessfulRecords(res?.successCount ?? 0);
                setShowSuccess(true);
                // reset state
                actions.resetForm();
              } catch (error: any) {
                console.log(error);
                if (
                  error?.errors &&
                  Object.values(error.Errors)[0] &&
                  Object.values(Object.values(error.Errors)[0] as any)[0]
                ) {
                  setErrorsList(error?.Errors.split('\r\n'));
                } else {
                  showToast('error', 'Error importing document, please try again.');
                }
              }
            }}
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              errors,
              handleSubmit,
              dirty,
              isValid,
              handleBlur,
            }) => {
              return (
                <Card className={classes.uploader} elevation={2}>
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    {props.templateDownloadUrl && (
                      <Box marginBottom="1.5rem" display="flex" alignItems="center" style={{ gap: '0.25rem' }}>
                        <Button
                          component="a"
                          href={props.templateDownloadUrl}
                          download
                          startIcon={<GetApp />}
                          color="primary"
                          variant="outlined"
                          size="small"
                        >
                          Download Template
                        </Button>
                        {props.documentationUrl !== undefined && (
                          <Tooltip title="Download documentation (Word)">
                            <IconButton
                              component="a"
                              href={props.documentationUrl}
                              download
                              size="small"
                              aria-label="Download bulk import documentation"
                            >
                              <InfoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    <Grid container>
                      <Grid container item spacing={1} xs={12} sm={6}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Import Name"
                            placeholder="Enter an import name..."
                            name="importName"
                            value={values.importName}
                            onBlur={handleBlur}
                            size="small"
                            required={true}
                            onChange={e => setFieldValue('importName', e.target.value)}
                            error={errors?.importName ? true : false}
                            helperText={errors?.importName}
                            inputProps={{ maxLength: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FileUploader
                            handleFile={val => {
                              setUploadedFile(val);
                              setFieldValue('fileName', val?.name ?? '');
                            }}
                            handleRemove={() => {
                              setUploadedFile(null);
                              setFieldValue('fileName', '');
                            }}
                            removeText={'Remove'}
                            templateFile={values.fileName}
                            isRequired={true}
                            dropzoneText="File to Import (.xlsx)"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Box marginTop="2rem">
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        color="secondary"
                        variant="contained"
                      >
                        Upload
                      </Button>
                    </Box>
                    {errorsList && (
                      <ConfirmationDialogue
                        open
                        id="errors-tier"
                        title="Upload Errors"
                        text={errorsList?.join('\r\n')}
                        hideCancel
                        onClose={() => {
                          setErrorsList(null);
                          setUploadedFile(null);
                          setFieldValue('fileName', '');
                        }}
                        onConfirm={() => {
                          setErrorsList(null);
                          setUploadedFile(null);
                          setFieldValue('fileName', '');
                        }}
                      />
                    )}
                    {showSuccess && (
                      <ConfirmationDialogue
                        open
                        id="success-tier"
                        title="Upload Success"
                        text={`Successfully updated ${successfulRecords} records`}
                        hideCancel
                        onClose={() => {
                          setShowSuccess(false);
                          setSuccessfulRecords(0);
                        }}
                        onConfirm={() => {
                          setShowSuccess(false);
                          setSuccessfulRecords(0);
                        }}
                      />
                    )}
                    {isSubmitting && (
                      <Loader position="centered" type="fullscreen">
                        {props.loaderMessage}
                      </Loader>
                    )}
                  </Form>
                </Card>
              );
            }}
          </Formik>
        </>
      </Container>
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  uploader: {
    padding: theme.spacing(2),
  },
}));
